"use client"

import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import moment from "moment"

import { PlanBadge } from "@/metrics/teachers/PlanBadge"
import { Link } from "@/components/ui/Link"
import { LoadingSpinner } from "@/components/LoadingSpinner"
import { Button } from "@/components/ui/Button"
import { StudentWorkInfoIcon } from "@/components/icons/StudentWorkInfoIcon"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"

import { TeachersMetricsPageController } from "./TeachersMetricsPageController"
import { TeacherUsageStats } from "../../../app/model/domain/teacher-usage-stats"
import { DataTable, SortingState } from "../DataTable"

const columnHelper = createColumnHelper<TeacherUsageStats>()

export interface TeachersMetricsPageProps {
  controller: TeachersMetricsPageController
}

export function TeachersMetricsPage({ controller }: TeachersMetricsPageProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "pagesWorkedOnThisYear", desc: true },
  ])

  const {
    data: organizationMetricsData,
    isPending: isOrganizationMetricsDataPending,
    isError: isOrganizationMetricsDataError,
  } = useQuery({
    queryKey: [
      controller.constructor.name,
      controller.getOrganizationMetricsData.name,
      controller.organizationId,
    ],
    queryFn: () =>
      controller.getOrganizationMetricsData(controller.organizationId),
  })

  const {
    data: teachersStats,
    isPending: isteachersStatsPending,
    isError: isteachersStatsError,
  } = useQuery({
    queryKey: [
      controller.constructor.name,
      controller.getTeachersUsageStats.name,
      controller.organizationId,
    ],
    queryFn: () => controller.getTeachersUsageStats(controller.organizationId),
  })

  const [isNoDataDialogOpen, setIsNoDataDialogOpen] = useState(true)

  const hasNoData =
    !isteachersStatsPending &&
    !isteachersStatsError &&
    teachersStats &&
    teachersStats.length === 0

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<TeacherUsageStats, any>[] = [
    columnHelper.accessor("fullName", {
      id: "name",
      header: "Teacher",
    }),
    ...(!organizationMetricsData?.isPro
      ? [
          columnHelper.accessor("isPro", {
            id: "isPro",
            header: "Plan",
            cell: ({ getValue }) => <PlanBadge isPro={getValue()} />,
          }),
        ]
      : []),

    columnHelper.accessor("assignmentsWorkedOnThisYear", {
      id: "assignmentsWorkedOnThisYear",
      header: "Assignments worked",
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),
    columnHelper.accessor("pagesWorkedOnThisYear", {
      id: "pagesWorkedOnThisYear",
      header: () => {
        return (
          <>
            Student Work Units <StudentWorkInfoIcon />
          </>
        )
      },
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),

    columnHelper.accessor("lastLogin", {
      id: "lastLogin",
      header: "Last logged in",
      sortUndefined: -1,
      cell: ({ getValue }) => moment(getValue()).format("MMM D, YYYY") ?? "–",
    }),
  ]

  function handleNudgeClick() {
    controller.nudgeTeachers(organizationMetricsData?.organization)
  }

  function handleInviteClick() {
    controller.inviteUsers(organizationMetricsData?.organization)
  }

  if (isOrganizationMetricsDataPending) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[calc((100vh-60px)/1.6)]">
        <LoadingSpinner />
      </div>
    )
  }

  if (isOrganizationMetricsDataError) {
    return (
      <p className="tw-text-sm tw-text-red-500">{controller.errorMessage}</p>
    )
  }

  const isContractAdmin = controller.isUserContractAdmin(
    organizationMetricsData.organization.contractId,
  )

  return (
    <div className="tw-h-[calc((100vh-60px)/1.6)] tw-flex tw-flex-col tw-p-[1.4em]">
      <Dialog
        open={hasNoData && isNoDataDialogOpen}
        onOpenChange={setIsNoDataDialogOpen}
        modal={false}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>No Data Found</DialogTitle>
            <DialogDescription>
              There is no data found for this organization. <br />
              Get started by inviting teachers to begin tracking.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="tw-justify-center"
              onClick={() => {
                setIsNoDataDialogOpen(false)
                if (isContractAdmin) {
                  handleNudgeClick()
                } else {
                  handleInviteClick()
                }
              }}
            >
              {isContractAdmin ? "Nudge Teachers" : "Invite Teachers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/account/metrics">
                All Schools
              </BreadcrumbLink>
              <BreadcrumbSeparator />
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <span className="tw-inline-flex tw-items-center">
                  {organizationMetricsData.organization.name}
                  <PlanBadge
                    className="tw-ml-2"
                    isPro={organizationMetricsData.isPro}
                  />
                </span>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {isContractAdmin ? (
          <Button onClick={handleNudgeClick}>Nudge Teachers</Button>
        ) : (
          <Button onClick={handleInviteClick}>Invite Teachers</Button>
        )}
      </div>
      <div>
        <div className="tw-flex tw-items-center tw-mt-10">
          <span className="tw-text-lg tw-text-title">Active Teachers</span>
          <span className="tw-ml-4 tw-text-sm tw-text-subtle">
            {controller.statsTimePeriod} - Present
          </span>
        </div>
        {isContractAdmin && (
          <Link
            href={`/account/contracts/${organizationMetricsData.organization.contractId}/users`}
          >
            Manage teachers
          </Link>
        )}
      </div>

      {isteachersStatsPending && <p className="tw-text-sm">Loading...</p>}

      {isteachersStatsError && (
        <p className="tw-text-sm tw-text-red-500">{controller.errorMessage}</p>
      )}

      {teachersStats && !isteachersStatsPending && !isteachersStatsError && (
        <DataTable
          columns={columns}
          data={teachersStats}
          state={{ sorting }}
          onSortingChange={(updater) => {
            const newSortingValue =
              updater instanceof Function ? updater(sorting) : updater
            controller.trackMetricsTableSort(newSortingValue)
            setSorting(updater)
          }}
        />
      )}
    </div>
  )
}
